import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner, Alert, Table } from '@/components/ui';

interface ValidationResult {
  success: boolean;
  totalRows: number;
  errors: Array<{
    row: number;
    message: string;
    data?: Record<string, any>;
  }>;
  validRows?: any[];
  validRowsCount?: number; // Number of rows with no errors
  errorRowsCount?: number; // Number of rows that have errors
  totalErrorCount?: number; // Total number of error messages (can be > errorRowsCount)
}

interface UploadResult {
  success: boolean;
  totalRows: number;
  successCount: number;
  errors: Array<{
    row: number;
    message: string;
    data?: Record<string, any>;
  }>;
}

interface ImportModalProps {
  visible: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<UploadResult>;
}

const ImportModal: React.FC<ImportModalProps> = ({ visible, onClose, onImport }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStartTime, setUploadStartTime] = useState<number | null>(null);
  const [currentStatus, setCurrentStatus] = useState<string>('');
  const [estimatedTime, setEstimatedTime] = useState<number | null>(null);
  const [originalFileContent, setOriginalFileContent] = useState<string | null>(null);
  const [originalHeaders, setOriginalHeaders] = useState<string[]>([]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setSelectedFile(null);
      setIsValidating(false);
      setIsUploading(false);
      setError(null);
      setValidationResult(null);
      setUploadResult(null);
      setUploadProgress(0);
      setUploadStartTime(null);
      setCurrentStatus('');
      setEstimatedTime(null);
      setOriginalFileContent(null);
      setOriginalHeaders([]);
    }
  }, [visible]);

  const handleDownloadTemplate = () => {
    const headers = [
      'service_area',
      'foreman_contract_area',
      'activity_number',
      'task_name',
      'priority',
      'planned_start_date',
      'planned_completion_date',
      'contract_year',
      'project_number',
      'foreman_area_name',
      'internal_comments',
      'status',
      'identified_quantity',
      'completion_accomplishment',
      'unit_of_measure',
      'base_rate',
      'rate_multiplier',
      'rate_adjustment',
      'rate_adjustment_comments',
      'task_type',
      'cost_type',
      'start_latitude',
      'start_longitude',
      'end_latitude',
      'end_longitude',
      'Asset Number',
      'record_id',
      'history_comments'
    ];

    // Example Row 1: Unit Rate task (cost_type not needed)
    // Dates: YYYY-MM-DD (planned_start_date, planned_completion_date) — avoids Excel misreading slash dates
    const sampleData1 = [
      '17',                    // service_area
      'CC',                    // foreman_contract_area
      '103301',                // activity_number
      'Shoulder Repair Segment A', // task_name
      'LOW',                   // priority
      '2024-07-01',            // planned_start_date
      '2026-12-15',            // planned_completion_date
      '6',                     // contract_year
      '',                      // project_number (optional)
      'Alexis Creek',          // foreman_area_name
      'Example Unit Rate task', // internal_comments
      'PLANNED',               // status
      '53.19',                 // identified_quantity
      '',                      // completion_accomplishment (optional)
      'rd km',                 // unit_of_measure
      '316.17',                // base_rate
      '',                      // rate_multiplier (optional)
      '',                      // rate_adjustment (optional)
      '',                      // rate_adjustment_comments (optional)
      'Unit Rate',             // task_type
      '',                      // cost_type (not needed for Unit Rate)
      '51.9245389',            // start_latitude (optional)
      '-123.1137601',          // start_longitude (optional)
      '51.9245389',            // end_latitude (optional)
      '-123.1137601',          // end_longitude (optional)
      '433',                   // Asset Number (optional)
      '',                      // record_id (optional)
      ''                       // history_comments (optional)
    ];

    // Example Row 2: Lump Sum task with Fixed Cost
    const sampleData2 = [
      '17',                    // service_area
      'CC',                    // foreman_contract_area
      '103301',                // activity_number
      'Bridge Deck Patch Package', // task_name
      'MEDIUM',                // priority
      '2024-07-01',            // planned_start_date
      '2024-09-01',            // planned_completion_date
      '6',                     // contract_year
      '',                      // project_number (optional)
      'Alexis Creek',          // foreman_area_name
      'Example Lump Sum task', // internal_comments
      'PLANNED',               // status
      '1',                     // identified_quantity (usually 1 for Lump Sum)
      '',                      // completion_accomplishment (optional)
      'Each',                  // unit_of_measure
      '5000',                  // base_rate (total value for Lump Sum)
      '',                      // rate_multiplier (optional)
      '',                      // rate_adjustment (optional)
      '',                      // rate_adjustment_comments (optional)
      'Lump Sum',              // task_type
      'Fixed Cost',            // cost_type (Fixed Cost or Cost Plus)
      '',                      // start_latitude (optional)
      '',                      // start_longitude (optional)
      '',                      // end_latitude (optional)
      '',                      // end_longitude (optional)
      '',                      // Asset Number (optional)
      '',                      // record_id (optional)
      ''                       // history_comments (optional)
    ];

    const csvContent = [
      headers.join(','),
      sampleData1.join(','),
      sampleData2.join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'task_import_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadTemplateWithErrors = () => {
    if (!validationResult || !originalFileContent || !originalHeaders.length) return;

    // Parse the original CSV
    const lines = originalFileContent.split('\n');
    if (lines.length === 0) return;

    // Create a map of row numbers to arrays of errors (to handle multiple errors per row)
    const errorMap = new Map<number, string[]>();
    validationResult.errors.forEach(err => {
      if (!errorMap.has(err.row)) {
        errorMap.set(err.row, []);
      }
      errorMap.get(err.row)!.push(err.message);
    });

    // Add Errors column header
    const headersWithErrors = [...originalHeaders, 'Errors'];

    // Process each row
    const rowsWithErrors: string[] = [headersWithErrors.join(',')];
    
    // Skip header row (index 0) and process data rows
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const rowNumber = i + 1; // Row numbers are 1-indexed (header is row 1, first data is row 2)
      const errors = errorMap.get(rowNumber) || [];
      // Join all errors with semicolon and space separator
      const allErrors = errors.join('; ');
      const errorText = allErrors ? `"${allErrors.replace(/"/g, '""')}"` : '';
      rowsWithErrors.push(`${line},${errorText}`);
    }

    const csvContent = rowsWithErrors.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'task_import_with_errors.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      setError('Please select a valid CSV file');
      setSelectedFile(null);
      setCurrentStatus('');
      return;
    }

    setSelectedFile(file);
    setError(null);
    setValidationResult(null);
    setUploadResult(null);
    setCurrentStatus(`Selected file: ${file.name}`);

    // Read file content for error CSV generation
    const reader = new FileReader();
    reader.onload = async (e) => {
      const content = e.target?.result as string;
      setOriginalFileContent(content);
      
      // Extract headers from first line
      const lines = content.split('\n');
      if (lines.length > 0) {
        setOriginalHeaders(lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '')));
      }
    };
    reader.readAsText(file);

    // Automatically validate the file
    setIsValidating(true);
    setCurrentStatus('Validating file...');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/tasks/quantified/validate', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        },
        credentials: 'same-origin'
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to validate file';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result: ValidationResult = await response.json();
      setValidationResult(result);

      // Get valid rows count from backend response, or calculate it correctly
      const validRowsCount = result.validRowsCount !== undefined 
        ? result.validRowsCount 
        : (result.validRows?.length || 0);
      
      if (result.errors.length === 0) {
        setCurrentStatus(`Validation successful: ${result.totalRows} rows are valid`);
        // Calculate estimated time: 3 seconds per task
        const estimatedSeconds = result.totalRows * 3;
        setEstimatedTime(estimatedSeconds);
      } else {
        const totalErrorCount = result.totalErrorCount !== undefined 
          ? result.totalErrorCount 
          : result.errors.length;
        setCurrentStatus(`Validation found ${totalErrorCount} error(s) in ${result.totalRows} rows`);
        setError(`Please fix the validation errors before uploading. ${validRowsCount} rows are valid.`);
      }
    } catch (err) {
      console.error('Error validating file:', err);
      setError(err instanceof Error ? err.message : 'Failed to validate file');
      setCurrentStatus('Validation failed');
    } finally {
      setIsValidating(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !validationResult || validationResult.errors.length > 0) {
      setError('Please fix all validation errors before uploading');
      return;
    }

    setIsUploading(true);
    setError(null);
    setUploadStartTime(Date.now());
    setUploadProgress(0);

    try {
      // Use validRowsCount from backend response, or calculate it correctly
      const validRowCount = validationResult.validRowsCount !== undefined 
        ? validationResult.validRowsCount 
        : (validationResult.validRows?.length || 0);
      setEstimatedTime(validRowCount * 3);
      setCurrentStatus(`Uploading ${validRowCount} tasks... Estimated time: ${Math.ceil(validRowCount * 3 / 60)} minutes`);

      // Setup progress simulation
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev < 90) {
            const elapsed = Date.now() - (uploadStartTime || Date.now());
            const remaining = (estimatedTime || 0) * 1000 - elapsed;
            const remainingMinutes = Math.ceil(remaining / 60000);
            setCurrentStatus(`Uploading... Approximately ${remainingMinutes} minute(s) remaining`);
            return prev + (90 - prev) * 0.05;
          }
          return prev;
        });
      }, 2000);

      const result = await onImport(selectedFile);
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadResult(result);

      const endTime = Date.now();
      const totalTimeSeconds = (endTime - (uploadStartTime || endTime)) / 1000;
      setCurrentStatus(`Upload completed in ${totalTimeSeconds.toFixed(1)} seconds`);

      if (result.successCount === 0) {
        setError('No tasks were uploaded successfully. Please check the errors below.');
      }
    } catch (err) {
      console.error('Error during upload:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload file');
      setUploadResult(null);
      setUploadProgress(0);
      setCurrentStatus('Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const handleReupload = () => {
    setSelectedFile(null);
    setError(null);
    setValidationResult(null);
    setUploadResult(null);
    setUploadProgress(0);
    setCurrentStatus('');
    setEstimatedTime(null);
    setOriginalFileContent(null);
    setOriginalHeaders([]);
    
    // Reset file input
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setIsValidating(false);
    setIsUploading(false);
    setError(null);
    setValidationResult(null);
    setUploadResult(null);
    setUploadProgress(0);
    setUploadStartTime(null);
    setCurrentStatus('');
    setEstimatedTime(null);
    setOriginalFileContent(null);
    setOriginalHeaders([]);
    onClose();
  };

  const hasValidationErrors = validationResult && validationResult.errors.length > 0;
  const canUpload = validationResult && validationResult.errors.length === 0 && !isUploading && !isValidating;

  return (
    <Modal show={visible} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Import Tasks</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!uploadResult ? (
          <>
            <div className="tw-mb-4">
              <h5>Instructions</h5>
              <p>
                1. Download the template CSV file below<br />
                2. <strong>Remove the sample data row</strong> (first row after headers)<br />
                3. Fill in your task data following the template format<br />
                4. Upload your completed CSV file (validation will run automatically)<br />
                5. Fix any validation errors and reupload if needed<br />
                6. Click "Upload Tasks" to process the file
              </p>
              <Alert variant="warning" className="tw-mb-3">
                <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                The first row in the template contains sample data and must be removed before importing your data.
              </Alert>
              <Alert variant="info" className="tw-mb-3">
                <i className="bi bi-info-circle tw-mr-2"></i>
                <strong>Note:</strong> You can use either <code>foreman_area_id</code> or <code>foreman_area_name</code> to specify the foreman area.
                If you're unsure of the ID, use the name. Make sure the rate adjustment comments are included if needed.
              </Alert>
              <Alert variant="info" className="tw-mb-3">
                <i className="bi bi-info-circle tw-mr-2"></i>
                <strong>Date formats:</strong> Use <code>YYYY-MM-DD</code> for <code>planned_start_date</code> and <code>planned_completion_date</code> (and optional alias <code>identified_date</code> for start).
                This avoids Excel misreading values like <code>07/01/26</code> as the wrong year.
              </Alert>
              <Alert variant="info" className="tw-mb-3">
                <i className="bi bi-info-circle tw-mr-2"></i>
                <strong>Currency formats:</strong> You can include dollar signs ($) and commas in currency fields like <code>base_rate</code> and <code>total_value</code>.
                For example, both <code>100.50</code> and <code>$100.50</code> are valid formats.
              </Alert>
              <Alert variant="info" className="tw-mb-3">
                <i className="bi bi-info-circle tw-mr-2"></i>
                <strong>Task Type:</strong> Use the <code>task_type</code> column to specify "Unit Rate" or "Lump Sum".
                If not provided, the system will infer from <code>base_rate</code> (0 = Lump Sum, otherwise Unit Rate).
                For Lump Sum tasks, also specify <code>cost_type</code> as "Fixed Cost" or "Cost Plus".
              </Alert>
              <Button
                variant="outline-primary"
                onClick={handleDownloadTemplate}
                className="tw-mb-3"
                disabled={isValidating || isUploading}
              >
                <i className="bi bi-download tw-mr-2"></i>
                Download Template
              </Button>
            </div>

            <div className="tw-mb-3">
              <Form.Label>Upload CSV File</Form.Label>
              <Form.Control
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                disabled={isValidating || isUploading}
              />
              {currentStatus && (
                <div className={`tw-mt-2 tw-text-sm ${isValidating ? 'tw-text-info' : hasValidationErrors ? 'tw-text-warning-text' : 'tw-text-success'}`}>
                  {currentStatus}
                </div>
              )}
              {error && <div className="tw-text-danger tw-mt-2">{error}</div>}
            </div>

            {isValidating && (
              <div className="tw-mt-3">
                <Spinner animation="border" size="sm" className="tw-text-primary tw-mr-2">Loading...</Spinner>
                <span className="tw-text-content-muted">Validating file...</span>
              </div>
            )}

            {validationResult && (
              <div className="tw-mt-3">
                <Alert variant={hasValidationErrors ? 'warning' : 'success'}>
                  <h6>Validation Results</h6>
                  <p>Total rows: {validationResult.totalRows}</p>
                  <p>Valid rows: {validationResult.validRowsCount !== undefined
                    ? validationResult.validRowsCount
                    : (validationResult.validRows?.length || 0)}</p>
                  <p>Rows with errors: {validationResult.errorRowsCount !== undefined
                    ? validationResult.errorRowsCount
                    : new Set(validationResult.errors.map(e => e.row)).size}</p>
                  <p>Total errors: {validationResult.totalErrorCount !== undefined
                    ? validationResult.totalErrorCount
                    : validationResult.errors.length}</p>
                  {!hasValidationErrors && estimatedTime && (
                    <p className="tw-mb-0">
                      <strong>Estimated upload time:</strong> {Math.ceil(estimatedTime / 60)} minute(s) ({estimatedTime} seconds)
                    </p>
                  )}
                </Alert>

                {hasValidationErrors && (
                  <div className="tw-mt-3">
                    <h6>Validation Errors</h6>
                    {validationResult.errors.length > 100 && (
                      <Alert variant="warning" className="tw-mb-2">
                        <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                        Showing first 100 of {validationResult.errors.length} errors
                      </Alert>
                    )}
                    <div className="tw-w-full tw-overflow-x-auto" style={{ maxHeight: "300px", overflowY: "auto" }}>
                      <Table size="sm" bordered>
                        <thead>
                          <tr>
                            <th>Row</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {validationResult.errors.slice(0, 100).map((error, index) => (
                            <tr key={index}>
                              <td>{error.row}</td>
                              <td>{error.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <div className="tw-mt-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleDownloadTemplateWithErrors}
                        className="tw-mr-2"
                      >
                        <i className="bi bi-download tw-mr-1"></i>
                        Download Template with Errors
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleReupload}
                      >
                        <i className="bi bi-upload tw-mr-1"></i>
                        Reupload File
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isUploading && (
              <div className="tw-mt-4">
                <h6>Upload Progress</h6>
                <div className="tw-flex tw-h-4 tw-overflow-hidden tw-rounded tw-bg-surface-alt tw-mb-2">
                  <div
                    className="tw-flex tw-flex-col tw-justify-center tw-overflow-hidden tw-bg-primary tw-text-center tw-whitespace-nowrap tw-text-white tw-transition-all"
                    role="progressbar"
                    style={{width: `${uploadProgress}%`}}
                    aria-valuenow={uploadProgress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  ></div>
                </div>
                <p className="tw-text-content-muted tw-text-sm">
                  {currentStatus || "Uploading tasks... This may take several minutes."}
                </p>
              </div>
            )}
          </>
        ) : (
          <div className="upload-results">
            <h5>Upload Results</h5>
            <Alert variant={uploadResult.successCount > 0 ? 'success' : 'danger'}>
              <p>Total rows processed: {uploadResult.totalRows}</p>
              <p>Successfully uploaded: {uploadResult.successCount}</p>
              <p>Failed rows: {uploadResult.errors.length}</p>
              <p className="tw-mb-0">{currentStatus}</p>
            </Alert>

            {uploadResult.errors.length > 0 && (
              <div className="tw-mt-3">
                <h6>Upload Errors</h6>
                {uploadResult.errors.length > 100 && (
                  <Alert variant="warning" className="tw-mb-2">
                    <i className="bi bi-exclamation-triangle tw-mr-2"></i>
                    Showing first 100 of {uploadResult.errors.length} errors
                  </Alert>
                )}
                <div className="tw-w-full tw-overflow-x-auto" style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <Table size="sm" bordered>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.errors.slice(0, 100).map((error, index) => (
                        <tr key={index}>
                          <td>{error.row}</td>
                          <td>{error.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!uploadResult ? (
          <>
            <Button variant="secondary" onClick={handleClose} type="button" disabled={isUploading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!canUpload || isUploading}
              type="button"
            >
              {isUploading ? (
                <>
                  <Spinner animation="border" size="sm" className="tw-mr-2" aria-hidden="true" />
                  Uploading...
                </>
              ) : (
                'Upload Tasks'
              )}
            </Button>
          </>
        ) : (
          <Button variant="primary" onClick={handleClose} type="button">
            Close
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ImportModal;
